/* Testimonial carousel — native scroll-snap for the track (free momentum and
   touch swipe), JS only for arrows, dots and autoplay. */
class TestimonialCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-tm-track]');
    if (!this.track) return;

    this.slides = Array.from(this.track.children);
    this.prevBtn = this.querySelector('[data-tm-prev]');
    this.nextBtn = this.querySelector('[data-tm-next]');
    this.dotsWrap = this.querySelector('[data-tm-dots]');
    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    this.autoplay = this.dataset.autoplay === 'true' && !this.reduceMotion.matches;
    this.delay = (parseFloat(this.dataset.autoplaySpeed) || 6) * 1000;

    this.buildDots();
    this.bind();
    this.update();
    this.startAutoplay();
  }

  disconnectedCallback() {
    this.stopAutoplay();
    this.resizeObserver?.disconnect();
    document.removeEventListener('visibilitychange', this.onVisibility);
  }

  /* How many slides fit at once, so dots map to pages rather than every card */
  get perView() {
    const slide = this.slides[0];
    if (!slide) return 1;
    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    const step = slide.getBoundingClientRect().width + gap;
    return Math.max(1, Math.round(this.track.clientWidth / step));
  }

  get pageCount() {
    return Math.max(1, this.slides.length - this.perView + 1);
  }

  get step() {
    const slide = this.slides[0];
    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    return slide ? slide.getBoundingClientRect().width + gap : this.track.clientWidth;
  }

  get index() {
    return Math.round(this.track.scrollLeft / this.step);
  }

  buildDots() {
    if (!this.dotsWrap) return;
    this.dotsWrap.innerHTML = '';
    for (let i = 0; i < this.pageCount; i += 1) {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'tm__dot';
      dot.setAttribute('aria-label', `Go to review ${i + 1}`);
      dot.addEventListener('click', () => this.goTo(i));
      this.dotsWrap.append(dot);
    }
    this.dots = Array.from(this.dotsWrap.children);
    this.dotsWrap.hidden = this.pageCount < 2;
  }

  goTo(i, smooth = true) {
    const max = this.pageCount - 1;
    const target = Math.min(Math.max(i, 0), max);
    this.track.scrollTo({
      left: target * this.step,
      behavior: smooth && !this.reduceMotion.matches ? 'smooth' : 'auto',
    });
  }

  next() {
    this.goTo(this.index >= this.pageCount - 1 ? 0 : this.index + 1);
  }

  prev() {
    this.goTo(this.index <= 0 ? this.pageCount - 1 : this.index - 1);
  }

  update = () => {
    const i = this.index;
    this.dots?.forEach((dot, n) => dot.setAttribute('aria-current', String(n === i)));
    if (this.prevBtn) this.prevBtn.disabled = i <= 0;
    if (this.nextBtn) this.nextBtn.disabled = i >= this.pageCount - 1;
    const single = this.pageCount < 2;
    this.classList.toggle('tm--static', single);
  };

  startAutoplay() {
    if (!this.autoplay) return;
    this.stopAutoplay();
    this.timer = setInterval(() => this.next(), this.delay);
  }

  stopAutoplay() {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
  }

  onVisibility = () => (document.hidden ? this.stopAutoplay() : this.startAutoplay());

  bind() {
    let raf;
    this.track.addEventListener('scroll', () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(this.update);
    }, { passive: true });

    this.prevBtn?.addEventListener('click', () => this.prev());
    this.nextBtn?.addEventListener('click', () => this.next());

    this.addEventListener('mouseenter', () => this.stopAutoplay());
    this.addEventListener('mouseleave', () => this.startAutoplay());
    this.addEventListener('focusin', () => this.stopAutoplay());
    this.addEventListener('focusout', () => this.startAutoplay());
    document.addEventListener('visibilitychange', this.onVisibility);

    this.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight') { e.preventDefault(); this.next(); }
      if (e.key === 'ArrowLeft') { e.preventDefault(); this.prev(); }
    });

    // Rebuild dots when the visible count changes at a breakpoint
    this.resizeObserver = new ResizeObserver(() => {
      clearTimeout(this.debounce);
      this.debounce = setTimeout(() => { this.buildDots(); this.update(); }, 150);
    });
    this.resizeObserver.observe(this.track);

    document.addEventListener('shopify:block:select', (e) => {
      const i = this.slides.indexOf(e.target);
      if (i === -1) return;
      this.stopAutoplay();
      this.goTo(Math.min(i, this.pageCount - 1));
    });
  }
}

if (!customElements.get('testimonial-carousel')) {
  customElements.define('testimonial-carousel', TestimonialCarousel);
}
